import React, { useState, useEffect, useCallback } from 'react';
import { KeyRound, X, Delete } from 'lucide-react';
import { api } from '../lib/api';
import { getElevated, setElevated } from '../lib/auth';
import { RecaptchaV2, executeV3 } from './RecaptchaWidget';
import { toast } from 'sonner';

let opener = null;

/** Pide autorización elevada. Resuelve con el token (o null si se cancela). */
export function requestPin(action) {
  const existing = getElevated();
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve) => {
    if (!opener) { resolve(null); return; }
    opener(action, resolve);
  });
}

export default function PinModal() {
  const [action, setAction] = useState(null);
  const [resolver, setResolver] = useState(null);
  const [digits, setDigits] = useState('');
  const [busy, setBusy] = useState(false);
  const [rc, setRc] = useState(null);
  const [rcToken, setRcToken] = useState('');

  useEffect(() => {
    if (action && rc === null) api.getAuthConfig().then(setRc).catch(() => setRc({ recaptchaEnabled: false }));
  }, [action, rc]);

  useEffect(() => {
    opener = (a, resolve) => { setAction(a); setResolver(() => resolve); setDigits(''); };
    return () => { opener = null; };
  }, []);

  const close = useCallback((token) => {
    resolver?.(token || null);
    setAction(null); setResolver(null); setDigits('');
  }, [resolver]);

  const submit = async (pin) => {
    if (rc?.recaptchaEnabled && rc.version === 'v2' && !rcToken) {
      toast.error('Marca "No soy un robot" para continuar');
      setDigits('');
      return;
    }
    setBusy(true);
    try {
      let token = rcToken;
      if (rc?.recaptchaEnabled && rc.version === 'v3') token = await executeV3(rc.siteKey, 'pin');
      const { elevatedToken } = await api.confirmPin(pin, token);
      setElevated(elevatedToken);
      close(elevatedToken);
    } catch (err) {
      setDigits('');
      if (rc?.recaptchaEnabled && rc.version === 'v2' && window.grecaptcha) { window.grecaptcha.reset(); setRcToken(''); }
      toast.error(err.response?.data?.detail || 'PIN incorrecto');
    } finally { setBusy(false); }
  };

  const press = (d) => {
    if (busy) return;
    const next = (digits + d).slice(0, 4);
    setDigits(next);
    if (next.length === 4) submit(next);
  };

  useEffect(() => {
    if (!action) return;
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') setDigits((p) => p.slice(0, -1));
      else if (e.key === 'Escape') close(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [action, digits, busy]);

  if (!action) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center px-4" data-testid="pin-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="bg-amber-50/70 px-6 py-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-black text-stone-800">PIN maestro</h2>
            <p className="text-xs text-stone-500 mt-0.5">Introduce tu PIN para autorizar: <strong className="text-stone-700">{action}</strong></p>
          </div>
          <button onClick={() => close(null)} className="text-stone-400 hover:text-stone-700 transition-colors" data-testid="pin-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-8 py-6">
          <div className="flex justify-center gap-3 mb-6" data-testid="pin-dots">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className={`w-3.5 h-3.5 rounded-full transition-colors ${i < digits.length ? 'bg-amber-500' : 'bg-stone-200'}`} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button key={n} onClick={() => press(String(n))} disabled={busy} className="border border-stone-200 rounded-xl py-3.5 text-lg font-bold text-stone-800 hover:border-amber-400 hover:bg-amber-50 transition-colors" data-testid={`pin-key-${n}`}>{n}</button>
            ))}
            <button onClick={() => setDigits('')} disabled={busy} className="rounded-xl py-3.5 text-sm font-bold text-stone-500 hover:bg-stone-100 transition-colors" data-testid="pin-key-clear">Borrar</button>
            <button onClick={() => press('0')} disabled={busy} className="border border-stone-200 rounded-xl py-3.5 text-lg font-bold text-stone-800 hover:border-amber-400 hover:bg-amber-50 transition-colors" data-testid="pin-key-0">0</button>
            <button onClick={() => setDigits((p) => p.slice(0, -1))} disabled={busy} className="rounded-xl py-3.5 flex items-center justify-center text-stone-500 hover:bg-stone-100 transition-colors" data-testid="pin-key-back"><Delete className="w-5 h-5" /></button>
          </div>
          {rc?.recaptchaEnabled && rc.version === 'v2' && <RecaptchaV2 siteKey={rc.siteKey} onToken={setRcToken} />}
          <div className="flex items-center justify-between mt-6">
            <button onClick={() => close(null)} className="text-sm text-stone-500 hover:text-stone-800 transition-colors" data-testid="pin-modal-cancel">Cancelar</button>
            <span className="text-[11px] text-stone-400">Sesión segura de 10 minutos</span>
          </div>
        </div>
      </div>
    </div>
  );
}
